import React from 'react';
import { formatWeddingDate } from '../personalization';
import { useT, useLang, localeOf } from '../i18n';
import { Icon } from './ui';
import { coupleThemeStyle } from './rsvp/coupleTheme';
import GuestProgram, { ProgramMoment } from './GuestProgram';

import { marketingUrl } from '../tenant';
import '../styles/guest.css';

interface Props {
  partner1: string;
  partner2: string;
  primaryColor?: string | null;
  fontFamily?: string | null;
  bodyFont?: string | null;
  heroImage?: string | null;
  heroImagePositionY?: number | null; // cadrage vertical 0-100 (photo recadrée sur ordinateur)
  weddingDate?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  program?: ProgramMoment[]; // moments du déroulé affichés aux invités
  onRespond?: () => void;
  preview?: boolean;
}

// Vue de la page d'invitation, sans logique de données. Utilisée à la fois par
// la vraie page (Landing) et par l'aperçu admin (LandingPreview) : couleur et
// polices viennent donc des props (valeurs éventuellement en cours d'édition).
const LandingView: React.FC<Props> = ({
  partner1,
  partner2,
  primaryColor,
  fontFamily,
  bodyFont,
  heroImage,
  heroImagePositionY,
  weddingDate,
  venueName,
  venueAddress,
  program = [],
  onRespond,
  preview = false,
}) => {
  const t = useT();
  const { lang } = useLang();

  const first = partner1 || t('Prénom');
  const second = partner2 || t('Prénom');
  const dateLabel = formatWeddingDate(weddingDate, localeOf(lang));

  // Les props font foi (aperçu) : sans police choisie, on revient aux polices du site.
  const style: React.CSSProperties = {
    ['--gu-heading' as any]: 'var(--font-display)',
    ['--gu-body' as any]: 'var(--font-ui)',
    ...coupleThemeStyle({ primaryColor, fontFamily, bodyFont }),
  };

  const classes = ['gu-landing', 'gu-theme', heroImage ? 'has-photo' : '', preview ? 'is-preview' : '']
    .filter(Boolean)
    .join(' ');

  const hasProgram = program.length > 0;
  const action = (variant: 'inline' | 'bar') => (
    <div className={`gu-landing-action is-${variant}`}>
      <button type="button" className="gu-cta" onClick={onRespond} tabIndex={preview ? -1 : undefined}>
        {t("Répondre à l'invitation")}
        <Icon name="arrow-right" size={18} />
      </button>
    </div>
  );
  const madeWith = (
    <footer className="gu-made-with">
      <a href={marketingUrl()} target="_blank" rel="noreferrer" tabIndex={preview ? -1 : undefined}>
        {t('Site créé gratuitement avec')} <strong>RSVP Please</strong>
      </a>
    </footer>
  );

  // Premier écran (photo + invitation), puis le programme pleine largeur s'il y en a un.
  // Le bouton de réponse est dans la colonne sur ordinateur, collé en bas de l'écran sur téléphone.
  return (
    <div className={classes} style={style}>
      <div className="gu-hero">
        {heroImage && (
          <div className="gu-landing-photo">
            <img
              src={heroImage}
              alt={`${first} & ${second}`}
              style={{ objectPosition: `center ${heroImagePositionY ?? 50}%` }}
            />
          </div>
        )}

        <main className="gu-landing-main">
          <div className="gu-landing-inner">
            <span className="gu-eyebrow">{t('Invitation')}</span>

            <h1 className="gu-landing-names">
              <span>{first}</span> <span className="gu-amp">&amp;</span> <span>{second}</span>
            </h1>

            <p className="gu-landing-tagline">{t('vous invitent à célébrer leur union')}</p>

            {dateLabel && (
              <div className="gu-date-rule">
                <span>{dateLabel}</span>
              </div>
            )}

            {(venueName || venueAddress) && (
              <div className="gu-venue">
                {venueName && <h2>{venueName}</h2>}
                {venueAddress && <p>{venueAddress}</p>}
              </div>
            )}

            {action('inline')}

            {hasProgram && (
              <button
                type="button"
                className="gu-link gu-to-program"
                tabIndex={preview ? -1 : undefined}
                onClick={() => document.getElementById('programme')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('Voir le programme')} ↓
              </button>
            )}

            <p className="gu-landing-closing">{t('Nous avons hâte de partager ce moment avec vous')}</p>

            {/* Sans programme : mention en bas de la colonne de texte */}
            {!hasProgram && madeWith}
          </div>
        </main>
      </div>

      {hasProgram && (
        <div className="gu-after">
          <GuestProgram moments={program} />
          {madeWith}
        </div>
      )}

      {action('bar')}
    </div>
  );
};

export default LandingView;
